import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import axios from 'axios';
import { BACKEND_URL, API_URL } from '../utils/apiConfig';

const API = API_URL;

const SimpleProviderDashboard = () => {
  const [activeTab, setActiveTab] = useState('calendar');
  const [appointments, setAppointments] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [newAvailability, setNewAvailability] = useState({
    start_time: '09:00',
    end_time: '17:00',
    staff_name: 'General'
  });

  // Mock provider info for testing
  const providerInfo = {
    id: 'demo-provider-001',
    name: 'Excellence Medical Center',
    facility_type: 'healthcare_clinic'
  };

  // Mock data for testing
  useEffect(() => {
    setAppointments([
      {
        id: 'apt-001',
        patient_name: 'Sarah Johnson',
        patient_phone: '+965 5XXX XXXX',
        appointment_date: '2025-01-31',
        appointment_time: '10:00',
        service: 'General Consultation',
        status: 'confirmed'
      }
    ]);

    setAvailability([
      {
        id: 'avail-001',
        date: '2025-01-31',
        start_time: '08:00',
        end_time: '12:00',
        staff_name: 'Dr. Michael Chen'
      }
    ]);
  }, []);

  const handleDateSelect = (selectInfo) => {
    console.log('Date selected:', selectInfo);
    setSelectedDate(selectInfo);
    setShowAvailabilityModal(true);
  };

  const handleEventClick = (clickInfo) => {
    console.log('Event clicked:', clickInfo.event);
    alert(`Event: ${clickInfo.event.title}\nTime: ${clickInfo.event.start}`);
  };

  const saveAvailability = async () => {
    console.log('Saving availability:', {
      date: selectedDate.startStr,
      ...newAvailability
    });

    // Add to local state for immediate display
    const newSlot = {
      id: `avail-${Date.now()}`,
      date: selectedDate.startStr,
      start_time: newAvailability.start_time,
      end_time: newAvailability.end_time,
      staff_name: newAvailability.staff_name
    };

    setAvailability(prev => [...prev, newSlot]);
    setShowAvailabilityModal(false);
    
    // Try to save to backend
    try {
      await axios.post(`${API}/provider/availability`, {
        date: selectedDate.startStr,
        ...newAvailability
      });
      console.log('Availability saved to backend');
    } catch (error) {
      console.error('Error saving to backend:', error);
    }
  };

  const getCalendarEvents = () => {
    const events = [];
    
    // Add appointments (blue)
    appointments.forEach(apt => {
      events.push({
        id: `apt-${apt.id}`,
        title: `${apt.patient_name} - ${apt.service}`,
        start: `${apt.appointment_date}T${apt.appointment_time}`,
        backgroundColor: '#3b82f6',
        borderColor: '#2563eb',
        extendedProps: {
          type: 'appointment'
        }
      });
    });
    
    // Add availability slots (green)
    availability.forEach(slot => {
      events.push({
        id: `avail-${slot.id}`,
        title: `Available - ${slot.staff_name}`,
        start: `${slot.date}T${slot.start_time}`,
        end: `${slot.date}T${slot.end_time}`,
        backgroundColor: '#10b981',
        borderColor: '#059669',
        extendedProps: {
          type: 'availability'
        }
      });
    });
    
    return events;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Provider Dashboard</h1>
          <p className="text-gray-600">
            Manage your availability and appointments - {providerInfo.name}
          </p>
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">📅 How to Set Availability:</h3>
            <ol className="text-blue-800 text-sm space-y-1">
              <li>1. Click on any empty time slot in the calendar below</li>
              <li>2. Set your start and end times in the popup</li>
              <li>3. Click "Save" to add the availability slot</li>
              <li>4. Green slots = Available, Blue slots = Booked appointments</li>
            </ol>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 mb-8">
          {[
            { id: 'calendar', label: 'Calendar & Availability', icon: '📅' },
            { id: 'appointments', label: 'Appointments', icon: '📋' },
            { id: 'settings', label: 'Settings', icon: '⚙️' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-6 py-3 font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'calendar' && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Calendar</h3>
              <p className="text-sm text-gray-600 mb-4">
                Click on empty time slots to set your availability. Current events: {getCalendarEvents().length}
              </p>
            </div>
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="timeGridWeek"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay'
              }}
              events={getCalendarEvents()}
              selectable={true}
              selectMirror={true}
              dayMaxEvents={true}
              weekends={true}
              select={handleDateSelect}
              eventClick={handleEventClick}
              height="600px"
              businessHours={{
                daysOfWeek: [1, 2, 3, 4, 5, 6], // Monday to Saturday
                startTime: '08:00',
                endTime: '20:00'
              }}
              selectConstraint="businessHours"
              slotMinTime="06:00:00"
              slotMaxTime="22:00:00"
            />
          </div>
        )}

        {activeTab === 'appointments' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Appointments ({appointments.length})
            </h2>
            
            {appointments.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-lg">
                <div className="text-6xl mb-4">📅</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No appointments yet</h3>
                <p className="text-gray-600">Set your availability first, then users can book with you</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {appointments.map((appointment) => (
                  <div key={appointment.id} className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {appointment.patient_name}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-gray-600"><strong>Date:</strong> {appointment.appointment_date}</p>
                        <p className="text-gray-600"><strong>Time:</strong> {appointment.appointment_time}</p>
                      </div>
                      <div>
                        <p className="text-gray-600"><strong>Service:</strong> {appointment.service}</p>
                        <p className="text-gray-600"><strong>Status:</strong> 
                          <span className="ml-2 px-2 py-1 rounded text-xs bg-green-100 text-green-800">
                            {appointment.status}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Provider Settings</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Provider Name</label>
                <input
                  type="text"
                  value={providerInfo.name}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Facility Type</label>
                <input
                  type="text"
                  value={providerInfo.facility_type}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Availability Modal */}
      {showAvailabilityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Set Availability</h3>
            <p className="text-sm text-gray-600 mb-4">
              Selected Date: {selectedDate?.startStr}
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Staff Member</label>
                <input
                  type="text"
                  value={newAvailability.staff_name}
                  onChange={(e) => setNewAvailability({...newAvailability, staff_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Dr. Smith, General"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                  <input
                    type="time"
                    value={newAvailability.start_time}
                    onChange={(e) => setNewAvailability({...newAvailability, start_time: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                  <input
                    type="time"
                    value={newAvailability.end_time}
                    onChange={(e) => setNewAvailability({...newAvailability, end_time: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={saveAvailability}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Save Availability
              </button>
              <button
                onClick={() => setShowAvailabilityModal(false)}
                className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleProviderDashboard;